import PropTypes from 'prop-types'
import React from 'react'
import { connect } from 'react-redux'
import focusable from '../../components/Focusable/Focusable'
import StdButton from '../../components/StdButton/StdButton'
import tts from '../../lib/TTS'
import { store } from '../../store'
import {
  ComcastErrorType,
  exitApp,
  triggerSentryError,
} from '../../../../shared/helpers.js'
import googleAnalytics4 from '../../lib/googleAnalytics.js'
import { withRouter } from '../../../../shared/components/WithRouter/WithRouter'

class ErrorScreen extends React.Component {
  constructor(props) {
    super(props)

    const errorState = store.getState().error
    this.errorStatus = errorState.error.status
    this.errorCode = errorState.error.code
    this.errorPath = errorState.error.content.errorPath
    this.errorType = errorState.error.errorType || ComcastErrorType.other

    this.shouldDisplayTryAgain = false
    this.shouldDisplayGoBack = true
    this.shouldDisplayExit = false
    this.errorText = ''
    this.goBackButtonText = 'Go Back'

    if (this.errorStatus === 'fatal') {
      this.errorTitle =
        'The PBS app encountered an error and needs to be restarted'
      this.errorText =
        'An error report has been submitted. We are sorry for the inconvenience. Visit help.pbs.org if the problem persists. Error: Fatal error'
      this.goBackButtonText = 'Restart App '
    } else if (this.errorStatus === 'geo') {
      this.errorTitle = 'Video not available'
      this.errorText = `This video is not available in your region (Error: ${this.errorCode})`
      this.goBackButtonText = 'Ok'
    } else if (this.errorStatus === 'drm') {
      this.errorTitle = 'Unable to Play Video'
      this.errorText = `An error ocurred while decrypting DRM content (Error: ${this.errorCode})`
      this.shouldDisplayTryAgain = true
    } else if (this.errorStatus === 'videoUnavailable') {
      this.errorTitle = 'Video not available'
      this.errorText = 'This video is currently not available for playback.'
    } else if (this.errorStatus === 404) {
      this.errorTitle = "We weren't able to find that content."
    } else if (this.errorStatus === 408) {
      this.errorTitle = 'A request timed out. Please try again.'
      this.shouldDisplayTryAgain = true
    } else if (this.errorStatus === 100) {
      this.errorTitle = 'Video Playback Error'
      this.errorText = `An unexpected error occurred with video playback (Error: ${this.errorCode})`
    } else if (this.errorStatus === 'NoSeeAllContent') {
      this.errorTitle = 'Unable to load content.'
      this.shouldDisplayExit = false
      this.shouldDisplayTryAgain = false
    } else {
      this.errorTitle = 'An unexpected error occurred.'
      this.errorText = 'Please try again, or exit and relaunch the app.'
      this.shouldDisplayTryAgain = !!this.errorPath
      this.shouldDisplayExit = true
    }

    this.tryAgainButtonRef = React.createRef()
    this.goBackButtonRef = React.createRef()
    this.exitButtonRef = React.createRef()

    triggerSentryError(
      `Error: ${errorState.error.code ?? 'Unknown error code'} (${
        this.errorStatus ?? 'Unknown error status'
      })`,
      'ErrorScreen.js',
      {
        error: errorState.error,
        errorLocation: 'ErrorScreen component',
        errorType: this.errorType,
      },
      true
    )
  }

  componentDidMount() {
    //Stop trying to deeplink
    store.dispatch({
      type: 'DEEPLINK_URL',
      payload: { url: '', force: false },
    })
    tts.pushTextToRead(
      `${this.errorTitle} ${this.errorText} ${
        COMPILING_FOR_COMCASTX1
          ? 'Need more help?. Visit pbs.org slash pbs comcast help.'
          : ''
      }`
    )
    if (this.shouldDisplayTryAgain) {
      setTimeout(() => {
        this.tryAgainButtonRef.current?.setFocus()
      }, 500)
    } else if (this.shouldDisplayGoBack) {
      setTimeout(() => {
        this.goBackButtonRef.current?.setFocus()
      }, 500)
    } else if (this.shouldDisplayExit) {
      setTimeout(() => {
        this.exitButtonRef.current?.setFocus()
      }, 500)
    }

    googleAnalytics4.sendPageViewEvent('Error Screen - Message')
  }

  goBack() {
    if (this.errorStatus === 'fatal') {
      window.location.href = '/'
    } else if (window.history.length < 3) {
      this.props.router.navigate('/')
    } else {
      this.props.router.navigate(-1)
    }
  }

  onKeyEvent(key) {
    let handled = false
    const componentType = window.focusedComponent.componentType

    if ('BACK' === key) {
      this.goBack()
    }

    if (componentType) {
      switch (componentType) {
        case 'tryAgainButton':
          if ('DOWN' === key) {
            if (this.shouldDisplayGoBack) {
              this.goBackButtonRef.current?.setFocus()
            } else if (this.shouldDisplayExit) {
              this.exitButtonRef.current?.setFocus()
            }

            handled = true
          }

          if ('ENTER' === key) {
            // Try loading the screen that produced the error again
            this.tryAgain()
            handled = true
          }
          break

        case 'goBackButton':
          if ('UP' === key) {
            if (this.shouldDisplayTryAgain) {
              this.tryAgainButtonRef.current?.setFocus()
            }
            handled = true
          }
          if ('DOWN' === key) {
            if (this.shouldDisplayExit) {
              this.exitButtonRef.current?.setFocus()
            }
            handled = true
          }

          if ('ENTER' === key) {
            // Take user back to screen before the error occurred
            this.goBack()
            handled = true
          }
          break

        case 'exitButton':
          if ('UP' === key) {
            if (this.shouldDisplayGoBack) {
              this.goBackButtonRef.current?.setFocus()
            } else if (this.shouldDisplayTryAgain) {
              this.tryAgainButtonRef.current?.setFocus()
            }
            handled = true
          }

          if ('ENTER' === key) {
            // Exit the app entirely
            exitApp()
            handled = true
          }
          break

        default:
          break
      }
    }

    return handled
  }

  tryAgain() {
    if (this.errorPath) {
      this.props.router.navigate(this.errorPath, { replace: true })
    }
  }

  render() {
    return (
      <div className="error-screen-container">
        <div className="text-container">
          <h1 className="title">{this.errorTitle}</h1>
          {this.errorStatus === 'fatal' ? (
            <div className="text text--fatal-error">
              <p>
                An error report has been submitted. We are sorry for the
                inconvenience. Visit{' '}
                <span className="text--fatal-error-url">help.pbs.org</span> if
                the problem persists.
              </p>
              <p>Error: Fatal error</p>
            </div>
          ) : (
            <p className="text">{this.errorText}</p>
          )}
        </div>
        <div className="error-screen-button-container">
          <StdButton
            label="Try Again Button"
            visible={this.shouldDisplayTryAgain}
            parent={this}
            componentType="tryAgainButton"
            ref={this.tryAgainButtonRef}
          >
            Try Again
          </StdButton>
          <StdButton
            label={`${this.goBackButtonText} button`}
            visible={this.shouldDisplayGoBack}
            parent={this}
            componentType="goBackButton"
            ref={this.goBackButtonRef}
          >
            {this.goBackButtonText}
          </StdButton>
          <StdButton
            label="Exit Button"
            visible={this.shouldDisplayExit}
            parent={this}
            componentType="exitButton"
            ref={this.exitButtonRef}
          >
            Exit
          </StdButton>
        </div>
        {COMPILING_FOR_COMCASTX1 && (
          <div
            style={{
              fontSize: '32px',
              marginTop: '40px',
              textAlign: 'center',
              lineHeight: '1.4',
            }}
          >
            <p>Need more help?</p>
            <p>pbs.org/pbscomcasthelp</p>
          </div>
        )}
      </div>
    )
  }
}

ErrorScreen.propTypes = {
  router: PropTypes.shape({
    navigate: PropTypes.func,
  }),
}

const mapStateToProps = (state) => {
  return {
    UID: state.device.UID,
    deviceId: state.device.deviceId,
    user: state.user,
  }
}

export default connect(
  mapStateToProps,
  null,
  null
)(withRouter(focusable(ErrorScreen)))
